import React, { useEffect, useState } from "react";
import {
    ArrowRightIcon,
    ChevronLeftIcon,
    InformationCircleIcon,
    ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import { useSystemLogo } from "../../context/systemConfigContext";

export default function DatosPersonalesPage() {
    const RESERVE_DRAFT_KEY = "reserve_draft_v1";
    const navigate = useNavigate();
    const location = useLocation();
    const { logo } = useSystemLogo();
    const [fullName, setFullName] = useState("");
    const [documentNumber, setDocumentNumber] = useState(
        location.state?.govId ?? ""
    );
    const [sex, setSex] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const readDraft = () => {
        try {
            const raw = sessionStorage.getItem(RESERVE_DRAFT_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? parsed : null;
        } catch {
            return null;
        }
    };

    const writeDraft = (patch) => {
        try {
            const current = readDraft() ?? {};
            const next = { ...current, ...patch };
            sessionStorage.setItem(RESERVE_DRAFT_KEY, JSON.stringify(next));
        } catch {
            // ignore
        }
    };

    const persistPersonalDraft = (data) => {
        writeDraft({
            personal: {
                fullName: (data?.fullName ?? "").toString(),
                govId: (data?.govId ?? "").toString(),
                sex: (data?.sex ?? "").toString(),
            },
        });
    };

    const updatePersonalDraft = (patch) => {
        const current = readDraft()?.personal ?? {};
        persistPersonalDraft({
            fullName: fullName,
            govId: documentNumber?.toString?.()
                ? documentNumber.toString()
                : String(documentNumber ?? ""),
            sex: sex,
            ...current,
            ...patch,
        });
    };

    useEffect(() => {
        const p = readDraft()?.personal ?? null;

        if (p && typeof p === "object") {
            setFullName(typeof p.fullName === "string" ? p.fullName : "");
            setSex(typeof p.sex === "string" ? p.sex : "");
            if (typeof p.govId === "string" && p.govId) {
                setDocumentNumber(p.govId);
            } else if (location.state?.govId) {
                setDocumentNumber(location.state.govId);
            }
        } else if (location.state?.govId) {
            setDocumentNumber(location.state.govId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canContinue =
        Boolean(fullName.trim()) &&
        Boolean(documentNumber.toString().trim()) &&
        Boolean(sex);

    const onNext = () => {
        if (!canContinue) {
            setErrorMessage("Completá los campos obligatorios.");
            return;
        }

        setErrorMessage("");
        const govId = documentNumber.trim();
        persistPersonalDraft({
            fullName: fullName.trim(),
            govId,
            sex,
        });
        navigate("/reserve/contact-data", {
            state: {
                govId,
                fullName: fullName.trim(),
                sex,
            },
        });
    };

    return (
        <div className="min-h-screen bg-[#F1F1FE] px-4 py-6 font-poppins md:flex md:items-center md:justify-center">
            <div className="w-full max-w-sm mx-auto md:max-w-[520px]">
                <div className="hidden md:block">
                    <div className="relative flex items-center justify-center">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="absolute left-0 flex items-center gap-2 text-[#000000] font-medium"
                        >
                            <div className="h-7 w-7 rounded-full bg-[#465FFF] flex items-center justify-center">
                                <ChevronLeftIcon className="h-4 w-4 text-white" />
                            </div>
                            <span>Atrás</span>
                        </button>
                        <img className="h-auto w-40" src={logo} alt="Logo" />
                    </div>
                </div>

                <div className="md:hidden">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-[#000000] font-medium"
                    >
                        <div className="h-7 w-7 rounded-full bg-[#465FFF] flex items-center justify-center">
                            <ChevronLeftIcon className="h-4 w-4 text-white" />
                        </div>
                        <span>Atrás</span>
                    </button>
                </div>

                <div className="mt-5 rounded-2xl bg-[#E3E2FF] px-4 py-4 flex gap-3">
                    <InformationCircleIcon className="h-6 w-6 text-[#465FFF] flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium text-[#465FFF] tracking-[0.3px]">
                            No encontramos su número en el sistema.
                        </p>
                        <p className="mt-1 text-xs text-[#000000] leading-4 tracking-[0.3px]">
                            Por favor, complete sus datos para que
                            podamos atenderlo.
                        </p>
                    </div>
                </div>

                <div className="mt-6 bg-white rounded-2xl shadow-sm px-6 py-7">
                    <h1 className="text-center text-[24px] font-semibold text-[#465FFF] tracking-[0.5px]">
                        Datos Personales
                    </h1>
                    <p className="mt-2 text-center font-medium text-sm text-[#344054] tracking-[0.3px]">
                        Paso 1 de 2
                    </p>

                    <div className="mt-4 flex justify-center">
                        <div className="flex items-center gap-3">
                            <div className="h-[6px] w-20 rounded-full bg-[#465FFF]" />
                            <div className="h-[6px] w-20 rounded-full bg-[#E6E7FF]" />
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className="block text-xs font-semibold text-[#000000]">
                            Nombre y Apellido *
                        </label>
                        <input
                            type="text"
                            value={fullName}
                            onChange={(e) => {
                                const v = e.target.value;
                                setFullName(v);
                                updatePersonalDraft({ fullName: v });
                            }}
                            placeholder="Escribí tu nombre y apellido"
                            className="mt-2 input-base px-3 py-3 text-sm"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="block text-xs font-semibold text-[#000000]">
                            Número de Cédula *
                        </label>
                        <input
                            type="text"
                            value={documentNumber}
                            onChange={(e) => {
                                const v = e.target.value;
                                setDocumentNumber(v);
                                updatePersonalDraft({ govId: v });
                            }}
                            placeholder="Ej: 5056538"
                            className="mt-2 input-base px-3 py-3 text-sm"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="block text-xs font-semibold text-[#000000]">
                            Sexo *
                        </label>
                        <div className="relative mt-2">
                            <select
                                value={sex}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setSex(v);
                                    updatePersonalDraft({ sex: v });
                                }}
                                className="input-base px-3 py-3 text-sm appearance-none bg-none"
                            >
                                <option value="" disabled>
                                    Seleccionar sexo
                                </option>
                                <option value="2">Femenino</option>
                                <option value="1">Masculino</option>
                            </select>
                            <ChevronDownIcon className="h-5 w-5 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onNext}
                        disabled={!canContinue}
                        className={`mt-6 w-full rounded-lg text-white font-medium py-3 flex items-center justify-center gap-2 ${
                            canContinue
                                ? "bg-[#465FFF]"
                                : "bg-[#B8B8B8] cursor-not-allowed"
                        }`}
                    >
                        <span>Siguiente</span>
                        <ArrowRightIcon className="h-5 w-5" />
                    </button>

                    {errorMessage ? (
                        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm">
                            {errorMessage}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
